import {StyleSheet} from '@react-pdf/renderer';
import {themeTokens} from '../theme/themeTokens';
import type {QuoteThemeTokens} from '../theme/themeTokens';
import type {QuoteFont, QuoteTemplate} from '../resolver/types';

// The document's palette, taken from the design. Five inks and one decorative border — not a ramp
// of white-blended tints, which is what this was and why the body text read blue-violet rather
// than the design's near-neutral black.
export const QUOTE_DOC_COLORS = {
	/** Body text, headings, signature rules. */
	ink: '#232334',
	/** Running paragraph copy. */
	body: '#3a3a4c',
	/** Supporting lines: totals labels, the cover's prepared-by. */
	secondary: '#4a4a5c',
	/** A line's own sub-note. */
	note: '#7a7a8c',
	/** Labels, column heads, page furniture. */
	muted: '#8b8b9b',
	/** The one decorative border. */
	border: '#d3d3df',
	/** The band a phase subtotal sits on. */
	tint: '#f5f3f9',
} as const;
export const INK = QUOTE_DOC_COLORS.ink;
const BODY = QUOTE_DOC_COLORS.body;
const SECONDARY = QUOTE_DOC_COLORS.secondary;
const MUTED = QUOTE_DOC_COLORS.muted;
const BORDER = QUOTE_DOC_COLORS.border;

// Resolve the regular / bold PostScript font names for the supported quote fonts.
const fontPair = (font?: QuoteFont) => {
	if (font === 'times') return {regular: 'Times-Roman', bold: 'Times-Bold'};
	if (font === 'courier') return {regular: 'Courier', bold: 'Courier-Bold'};
	return {regular: 'Helvetica', bold: 'Helvetica-Bold'};
};

/**
 * The face a heading weight resolves to. The Base-14 set has no weight axis - a weight is a
 * different face name - so `fontWeight` does nothing here and the weight has to pick the face.
 * Anything at semibold or above takes the bold face; Classic's 400 stays regular.
 */
const headingFace = (font: QuoteFont, weight: number): string => {
	const pair = fontPair(font);
	return weight >= 600 ? pair.bold : pair.regular;
};

// Style factory only — every consumer builds its styles from the document's font, so there is
// no static default export to drift out of sync with the factory.
/**
 * `tokens` carries the theme: the heading voice, the cover band and the rule treatment. Passing it
 * is what keeps the PDF and the live preview from drifting, because both read the same function.
 */
export const buildStyles = (font?: QuoteFont, tokens?: QuoteThemeTokens) => {
	const {regular, bold} = fontPair(font);
	// The heading face is the theme's, not the body's: Classic is the serif voice, and its
	// weight picks the face because the Base-14 set carries no weight axis.
	const headingFamily = tokens ? headingFace(tokens.headingFont, tokens.headingWeight) : bold;
	// Every line in the band takes the theme's own foreground, so the band can be painted in the
	// accent, on white or on a tint without any rule here naming a theme.
	const coverInk = tokens?.coverForeground ?? INK;
	const coverAlign = tokens?.coverAlign === 'center' ? 'center' : 'flex-start';
	return StyleSheet.create({
		bold: {fontFamily: bold},
		// ---- Page chrome -------------------------------------------------------
		page: {
			paddingTop: 84,
			paddingBottom: 72,
			paddingHorizontal: 48,
			fontFamily: regular,
			fontSize: 10,
			color: INK,
		},
		band: {
			position: 'absolute',
			top: 28,
			left: 48,
			right: 48,
			flexDirection: 'row',
			justifyContent: 'space-between',
			alignItems: 'center',
			paddingBottom: 10,
			borderBottom: `1px solid ${BORDER}`,
		},
		bandCover: {
			position: 'absolute',
			top: 28,
			left: 48,
			right: 48,
			flexDirection: 'row',
			justifyContent: 'flex-end',
			alignItems: 'center',
			paddingBottom: 10,
		},
		bandRun: {flexDirection: 'row', alignItems: 'center'},
		bandLogoImage: {height: 14, width: 42, objectFit: 'contain', objectPosition: 'left', marginRight: 8},
		bandLogoWord: {fontSize: 9, fontFamily: bold, color: SECONDARY, marginRight: 8},
		bandRunText: {fontSize: 8, color: MUTED},
		bandConfidential: {fontSize: 7, letterSpacing: 1.1, textTransform: 'uppercase', color: MUTED},
		content: {flex: 1},
		foot: {
			position: 'absolute',
			bottom: 26,
			left: 48,
			right: 48,
			flexDirection: 'row',
			justifyContent: 'space-between',
			borderTop: `1px solid ${BORDER}`,
			paddingTop: 8,
		},
		footText: {fontSize: 8, color: MUTED},
		// ---- Shared section pieces ----------------------------------------------
		section: {marginBottom: 22},
		// The rule belongs on the row, not the text: Classic rules its headings in the accent and
		// Minimal in grey, and the design runs that rule the full width of the column rather than
		// underlining the words. Modern draws none. The preview reads the same two tokens.
		sectionLabelRow: {
			flexDirection: 'row',
			alignItems: 'center',
			marginBottom: 13,
			...(tokens?.headingRuleColor
				? {paddingBottom: tokens.headingPaddingBottom, borderBottom: `1px solid ${tokens.headingRuleColor}`}
				: {}),
		},
		// No leading bar: the design heads a section with the word alone, in the accent.
		sectionLabelBar: {width: 16, height: 2, borderRadius: 1, marginRight: 8},
		sectionLabelText: {
			fontSize: tokens?.headingSize ?? 9,
			fontFamily: headingFamily,
			letterSpacing: tokens?.headingLetterSpacing ?? 1.3,
			textTransform: tokens?.headingTransform ?? 'uppercase',
			// A PDF has no reflow: an unbroken title has nowhere to wrap and simply carries on past
			// the right edge of the page. Two lines is as much as a heading is allowed to take.
			maxLines: 2,
			textOverflow: 'ellipsis',
		},
		paragraph: {fontSize: 10, lineHeight: 1.65, marginBottom: 9, color: BODY},
		// ---- Cover ----------------------------------------------------------------
		// The cover page carries no padding of its own: the band runs to the sheet's top and side
		// edges, and everything below it is inset by the body.
		coverPage: {paddingTop: 0, paddingBottom: 72, paddingHorizontal: 0, fontFamily: regular, fontSize: 10, color: INK},
		coverContent: {flex: 1},
		coverBand: {
			height: tokens?.coverHeight ?? 294,
			padding: 42,
			justifyContent: 'space-between',
			backgroundColor: tokens?.coverBackground ?? '#ffffff',
			color: coverInk,
			...(tokens?.coverRuleColor ? {borderBottom: `1px solid ${tokens.coverRuleColor}`} : {}),
		},
		coverLogoImage: {height: 24, width: 72, objectFit: 'contain', objectPosition: 'left'},
		coverLogoWord: {fontSize: 14, fontFamily: bold, color: coverInk},
		coverTitleBlock: {alignItems: coverAlign, textAlign: coverAlign === 'center' ? 'center' : 'left'},
		coverEyebrow: {
			fontSize: 9.75,
			letterSpacing: tokens?.coverKickerLetterSpacing ?? 2.2,
			textTransform: 'uppercase',
			color: coverInk,
			opacity: 0.75,
			marginBottom: 10.5,
		},
		coverTitle: {
			fontSize: tokens?.coverTitleSize ?? 34.5,
			fontFamily: headingFamily,
			lineHeight: 1.08,
			letterSpacing: -0.4,
			color: coverInk,
			marginBottom: 10.5,
		},
		coverFor: {fontSize: 11.25, color: coverInk, opacity: 0.8},
		coverBody: {flex: 1, paddingTop: 36, paddingHorizontal: 42},
		coverMeta: {flexDirection: 'row', flexWrap: 'wrap'},
		coverMetaCell: {width: '50%', marginBottom: 21, paddingRight: 24},
		coverMetaLabel: {fontSize: 7.9, letterSpacing: 1.2, textTransform: 'uppercase', color: '#8b8b9b', marginBottom: 4},
		coverMetaValue: {fontSize: 10.9, color: '#232334'},
		// ---- Estimate table -------------------------------------------------------
		tableHeadRow: {
			flexDirection: 'row',
			borderBottom: `1.5px solid ${tokens?.ruleColor ?? BORDER}`,
			backgroundColor: tokens?.tableHeadBackground ?? 'transparent',
			// A filled head needs breathing room and has to clear the page padding; an unthemed
			// (transparent) head keeps the original tight rule-only treatment.
			marginHorizontal: tokens ? -8 : 0,
			paddingHorizontal: tokens ? 8 : 0,
			paddingTop: tokens ? 7 : 0,
			paddingBottom: 7,
		},
		th: {
			fontSize: 8,
			fontFamily: bold,
			letterSpacing: 0.8,
			textTransform: 'uppercase',
			color: tokens?.tableHeadColor ?? MUTED,
		},
		thNum: {textAlign: 'right'},
		colLabel: {flex: 1, paddingRight: 8},
		colNum: {width: 74},
		phaseRow: {flexDirection: 'row', alignItems: 'flex-end', paddingTop: 12, paddingBottom: 3},
		phaseCell: {flexDirection: 'row', alignItems: 'flex-end'},
		phaseIndex: {fontSize: 7.5, fontFamily: bold, letterSpacing: 0.8, textTransform: 'uppercase', marginRight: 8},
		phaseName: {fontSize: 10, fontFamily: bold, letterSpacing: 0.8, textTransform: 'uppercase'},
		phaseSubtotal: {fontSize: 10.5, fontFamily: bold, textAlign: 'right'},
		deliverableRow: {flexDirection: 'row', alignItems: 'center', paddingTop: 11, paddingBottom: 2, paddingLeft: 10},
		deliverableLabel: {fontSize: 7, fontFamily: bold, letterSpacing: 0.9, textTransform: 'uppercase', marginRight: 8},
		deliverableName: {fontSize: 8.5, fontFamily: bold},
		itemRow: {flexDirection: 'row', paddingTop: 8, paddingBottom: 8, borderBottom: `1px solid ${BORDER}`},
		itemLabelCell: {flexDirection: 'row', alignItems: 'center'},
		itemName: {fontSize: 10},
		itemNum: {fontSize: 10, textAlign: 'right'},
		itemTotal: {fontSize: 10, textAlign: 'right'},
		expenseTag: {
			fontSize: 7,
			fontFamily: bold,
			letterSpacing: 0.9,
			textTransform: 'uppercase',
			color: MUTED,
			marginRight: 7,
		},
		expensesTotalRow: {flexDirection: 'row', paddingTop: 8, paddingBottom: 8, borderBottom: `1px solid ${BORDER}`},
		// The design sets a phase's subtotal apart on its own tinted band.
		phaseSubtotalRow: {
			flexDirection: 'row',
			paddingTop: 9,
			paddingBottom: 9,
			backgroundColor: QUOTE_DOC_COLORS.tint,
			borderTop: `1px solid ${BORDER}`,
			borderBottom: `1px solid ${BORDER}`,
		},
		expensesTotalLabel: {fontSize: 10, fontFamily: bold},
		rollupRow: {flexDirection: 'row', paddingTop: 10, paddingBottom: 10, borderBottom: `1px solid ${BORDER}`},
		rollupName: {fontSize: 10},
		// ---- Estimate totals ------------------------------------------------------
		totalsWrap: {flexDirection: 'row', justifyContent: 'flex-end', marginTop: 18},
		totalsGrid: {width: 250},
		totalsRow: {flexDirection: 'row', justifyContent: 'space-between', marginBottom: 7},
		totalsLabel: {fontSize: 10, color: SECONDARY},
		totalsValue: {fontSize: 10, textAlign: 'right'},
		totalsGrandRow: {
			flexDirection: 'row',
			justifyContent: 'space-between',
			borderTop: `1.5px solid ${tokens?.grandTotalRuleColor ?? INK}`,
			paddingTop: 9,
			marginTop: 3,
		},
		totalsGrandLabel: {fontSize: 12, fontFamily: bold},
		totalsGrandValue: {fontSize: 12, fontFamily: bold, textAlign: 'right'},
		// ---- Terms ----------------------------------------------------------------
		termRow: {flexDirection: 'row', marginBottom: 13},
		termNumber: {width: 16, fontSize: 11, fontFamily: bold},
		termBody: {flex: 1},
		termHeading: {fontSize: 10, fontFamily: bold, marginBottom: 3},
		termText: {fontSize: 9.5, lineHeight: 1.6, color: BODY},
		// ---- Agreement / signatures ----------------------------------------------
		signGrid: {flexDirection: 'row', marginTop: 28},
		signParty: {flex: 1},
		signPartyFirst: {flex: 1, marginRight: 36},
		signOrg: {fontSize: 11, fontFamily: bold, marginBottom: 18},
		signField: {marginTop: 22},
		signSignatureSpace: {height: 22},
		signLine: {borderBottom: `1px solid ${INK}`, marginBottom: 5},
		signLabel: {fontSize: 8, letterSpacing: 0.6, textTransform: 'uppercase', color: MUTED},
		signNote: {marginTop: 28, fontSize: 8.5, lineHeight: 1.5, color: MUTED},
	});
};

/**
 * The stylesheet a template renders with. Every page builds from this rather than from the raw
 * font and accent, so the theme cannot reach one page and miss another.
 */
export const stylesFor = (template: QuoteTemplate): ReturnType<typeof buildStyles> =>
	buildStyles(
		template.branding.font,
		themeTokens(template.layout.theme, template.branding.accentColor, template.branding.font)
	);
